import type { RoutingLoadOverviewResponse, RoutingLoadSettingSource } from '@nadoutong/contracts';
import { useRoutingLoadOverview } from '../../api/useRoutingLoad.ts';
import { useT } from '../../i18n/index.ts';
import { StatusLamp } from '../../ui/index.ts';
import type { LampTone } from '../../ui/index.ts';
import { LAYER_SWITCH_FIELDS } from './loadSettingsFields.ts';
import type { LayerSwitchField } from './loadSettingsFields.ts';

const LAYER_LABEL: Record<LayerSwitchField, 'strip.events' | 'strip.l2' | 'strip.l3' | 'strip.l4'> = {
  events_enabled: 'strip.events',
  l2_enabled: 'strip.l2',
  l3_enabled: 'strip.l3',
  l4_enabled: 'strip.l4',
};

function layerLampTone(
  field: LayerSwitchField,
  overview: RoutingLoadOverviewResponse | undefined,
): LampTone {
  if (overview === undefined) return 'off';
  const killed =
    (field === 'l2_enabled' && overview.l2_env_kill_switch) ||
    (field === 'l3_enabled' && overview.l3_env_kill_switch) ||
    (field === 'l4_enabled' && overview.l4_env_kill_switch);
  const warned = field === 'l3_enabled' && (overview.warnings ?? []).includes('l3_without_l2');
  if (killed || warned) return 'warn';
  return overview.effective[field] ? 'ok' : 'off';
}

function sourceTitle(source: RoutingLoadSettingSource | undefined): string {
  return source ?? 'default';
}

export function RoutingLoadStrip({
  onOpen,
}: {
  readonly onOpen: () => void;
}): JSX.Element {
  const t = useT();
  const overview = useRoutingLoadOverview();
  const data = overview.data;
  const hasWarn =
    data !== undefined && (
      data.l2_env_kill_switch ||
      data.l3_env_kill_switch ||
      data.l4_env_kill_switch ||
      (data.warnings ?? []).length > 0
    );

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-11 flex-wrap items-center gap-x-4 gap-y-2 rounded-md bg-well px-3 py-2 text-left text-dense transition-colors duration-120 hover:bg-active"
      data-testid="tenant-routing-load-strip"
    >
      <span className="text-micro font-bold text-paper">
        {t('tenant_routing.load.strip_label')}
      </span>
      {LAYER_SWITCH_FIELDS.map((field) => (
        <span
          key={field}
          className="inline-flex items-center gap-1.5 border-l border-line-soft/20 pl-4"
          title={sourceTitle(data?.sources[field])}
          data-testid={`tenant-routing-load-lamp-${field}`}
        >
          <StatusLamp
            tone={layerLampTone(field, data)}
            size="sm"
            testId={`tenant-routing-load-lamp-tone-${field}`}
          />
          <span className="text-micro font-bold text-paper-low">
            {t(`tenant_routing.load.${LAYER_LABEL[field]}`)}
          </span>
        </span>
      ))}
      {hasWarn ? (
        <span
          className="text-micro font-bold text-warn"
          data-testid="tenant-routing-load-strip-warn"
        >
          {t('tenant_routing.load.strip.warn')}
        </span>
      ) : null}
    </button>
  );
}
